import React, { useState, useEffect } from "react";
import { Plus, Save } from "lucide-react";
import { usersApi, clientsApi } from "../../utils/api";
import { userClientApi, userClientsApi } from "../../utils/api";
import { CLIENT_ACCESS_ROLES } from "../../constants";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const ClientToUser = () => {
  const [associations, setAssociations] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    user_id: "",
    client_id: "",
    role: "viewer",
  });

  useEffect(() => {
    fetchAssociations();
    fetchUsers();
    fetchClients();
  }, []);

  const fetchAssociations = async () => {
    setLoading(true);
    try {
      // TODO: Implement API call to fetch user-client associations
      const data = await userClientApi.getAll();
      setAssociations(data);
    } catch (error) {
      console.error("Error fetching associations:", error);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await usersApi.associateClient(
        formData.user_id,
        formData.client_id,
        formData.role,
      );
      fetchAssociations();
      resetForm();
    } catch (error) {
      console.error("Error saving association:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (userId, clientId) => {
    if (!window.confirm("Remove this user-client association?")) return;
    setLoading(true);
    try {
      // TODO: Implement delete API call
      console.log("Delete association:", userId, clientId);
      fetchAssociations();
    } catch (error) {
      console.error("Error deleting association:", error);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      client_id: "",
      role: "viewer",
    });
    setShowForm(false);
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.name : clientId;
  };

  const filteredAssociations = associations.filter(
    (assoc) =>
      getUserName(assoc.user_id)
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      getClientName(assoc.client_id)
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchAssociations}
          placeholder="Search associations..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Association
        </PrimaryButton>
      </div>

      {/* Form */}
      <FormModal
        title="Associate User with Client"
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                User *
              </label>
              <select
                required
                value={formData.user_id}
                onChange={(e) =>
                  setFormData({ ...formData, user_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select user</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client *
              </label>
              <select
                required
                value={formData.client_id}
                onChange={(e) =>
                  setFormData({ ...formData, client_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Access Role *
            </label>
            <select
              required
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {CLIENT_ACCESS_ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Association"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredAssociations.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No associations found. Create one to get started!
                </td>
              </tr>
            ) : (
              filteredAssociations.map((assoc) => (
                <tr
                  key={`${assoc.user_id}-${assoc.client_id}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {getUserName(assoc.user_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {getClientName(assoc.client_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {assoc.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <ActionButtons
                      onEdit={() => {}}
                      onDelete={() =>
                        handleDelete(assoc.user_id, assoc.client_id)
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientToUser;
